import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, Row, Col, message, Spin, Divider } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { get, post } from '../../utils/fetch';
import styles from './JiraUserMappingModal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  integrationId: string | null;
  projectIntegrationId: string | null;
  teamId: string | null;
  bugherdUsers: any[];
  container?: HTMLElement | (() => HTMLElement);
}

const LinearUserMappingModal: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  integrationId,
  projectIntegrationId,
  teamId,
  bugherdUsers,
  container,
}) => {
  const [loading, setLoading] = useState(false);
  const [linearUsers, setLinearUsers] = useState<any[]>([]);
  const [mappings, setMappings] = useState<
    { linearUserId: string; bugherdUserId: string }[]
  >([{ linearUserId: '', bugherdUserId: '' }]);

  useEffect(() => {
    if (open && integrationId && teamId) {
      loadData();
    }
  }, [open, integrationId, teamId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchLinearUsers(), fetchCurrentSettings()]);
    setLoading(false);
  };

  const fetchLinearUsers = async () => {
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}&site_name=members:${teamId}`
      );
      setLinearUsers(results);
    } catch (error) {
      message.error('Failed to fetch Linear team members.');
    }
  };

  const fetchCurrentSettings = async () => {
    if (!projectIntegrationId) {
      setMappings([{ linearUserId: '', bugherdUserId: '' }]);
      return;
    }
    try {
      const result = await get(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/${projectIntegrationId}`
      );
      if (result?.settings?.user_mapping) {
        const existing = Object.entries(result.settings.user_mapping).map(
          ([linearId, bhId]) => ({
            linearUserId: linearId,
            bugherdUserId: String(bhId),
          })
        );
        setMappings(
          existing.length ? existing : [{ linearUserId: '', bugherdUserId: '' }]
        );
      }
    } catch (error) {
      message.error('Failed to fetch current user mapping.');
    }
  };

  const handleAutomap = () => {
    const automapped = linearUsers
      .map(linearUser => {
        const matched = bugherdUsers.find(
          bu =>
            bu.email?.toLowerCase() === linearUser.value?.toLowerCase() ||
            bu.display_name?.toLowerCase() ===
              linearUser.label?.split(' (')[0]?.toLowerCase()
        );
        return matched
          ? { linearUserId: linearUser.value, bugherdUserId: String(matched.id) }
          : null;
      })
      .filter(Boolean) as { linearUserId: string; bugherdUserId: string }[];

    if (!automapped.length) {
      message.info('No automatic matches found by email or display name.');
      return;
    }
    setMappings([...automapped, { linearUserId: '', bugherdUserId: '' }]);
    message.success(`Auto-mapped ${automapped.length} user(s).`);
  };

  const addMapping = () =>
    setMappings(prev => [...prev, { linearUserId: '', bugherdUserId: '' }]);

  const removeMapping = (index: number) =>
    setMappings(prev => prev.filter((_, i) => i !== index));

  const updateMapping = (
    index: number,
    field: 'linearUserId' | 'bugherdUserId',
    value: string
  ) => {
    setMappings(prev =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  const saveMapping = async () => {
    const userMapping: Record<string, string> = {};
    const userDisplayNames: Record<string, string> = {};

    mappings.forEach(({ linearUserId, bugherdUserId }) => {
      if (linearUserId && bugherdUserId) {
        userMapping[linearUserId] = bugherdUserId;

        // Prefer the Linear @handle (displayName) for mention matching; fall back to the
        // name extracted from the label for accounts that don't return a handle.
        const option = linearUsers.find(u => u.value === linearUserId);
        if (option) {
          const handle = (option.displayName || option.label.split(' (')[0]).trim();
          if (handle) userDisplayNames[linearUserId] = handle;
        }
      }
    });

    try {
      await post(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/save_settings?integration_code=Linear`,
        {
          project_integration_id: projectIntegrationId,
          settings: { user_mapping: userMapping, user_display_names: userDisplayNames },
        }
      );
      message.success('User mapping saved.');
      onClose();
    } catch (error) {
      message.error('Failed to save user mapping.');
    }
  };

  return (
    <Modal
      title="User Mapping"
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      getContainer={container}
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <p>
            Map BugHerd users to their Linear counterparts so actions are
            attributed correctly in Linear.
          </p>
          <Button
            onClick={handleAutomap}
            style={{ marginBottom: 16 }}
            icon={<span>⚡</span>}
          >
            Automap by email
          </Button>
          <Divider />
          {mappings.map((mapping, index) => (
            <Row key={index} gutter={8} style={{ marginBottom: 8 }} align="middle">
              <Col span={11}>
                <Select
                  showSearch
                  placeholder="Linear user"
                  optionFilterProp="label"
                  options={linearUsers}
                  value={mapping.linearUserId || undefined}
                  onChange={val => updateMapping(index, 'linearUserId', val)}
                  style={{ width: '100%' }}
                  allowClear
                />
              </Col>
              <Col span={2} style={{ textAlign: 'center' }}>
                →
              </Col>
              <Col span={9}>
                <Select
                  showSearch
                  placeholder="BugHerd user"
                  optionFilterProp="label"
                  options={bugherdUsers.map(u => ({
                    value: String(u.id),
                    label: u.email
                      ? `${u.display_name} (${u.email})`
                      : u.display_name,
                  }))}
                  value={mapping.bugherdUserId || undefined}
                  onChange={val => updateMapping(index, 'bugherdUserId', val)}
                  style={{ width: '100%' }}
                  allowClear
                />
              </Col>
              <Col span={2}>
                <MinusCircleOutlined
                  onClick={() => removeMapping(index)}
                  style={{ color: '#ff4d4f', cursor: 'pointer' }}
                />
              </Col>
            </Row>
          ))}
          <Button
            type="dashed"
            onClick={addMapping}
            icon={<PlusOutlined />}
            style={{ marginTop: 8 }}
          >
            Sync another user
          </Button>
          <Divider />
          <Row justify="end">
            <Button type="primary" onClick={saveMapping}>
              Save mapping
            </Button>
          </Row>
        </>
      )}
    </Modal>
  );
};

export default LinearUserMappingModal;
